import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { ItemAiChatSessionSchema, ItemAiChatTurnSchema } from '../item-ai-chat';
import { ExternalAuthErrorResponses, ExternalErrorSchema, ExternalRouteMetadata, ExternalTag } from './external.schemas';

const c = initContract();

const AI_CHAT_ITEM_ACCESS = 'The declaration must belong to a batch of a customer you have API access to, and the goods item must be part of the declaration.';

const AiChatItemBodySchema = z.object({
  declarationId: z.string().describe('_id of the declaration'),
  goodsShipmentItemId: z.string().describe('_id of the goods item (goodsShipmentItems._id of the declaration)'),
});

const AiChatErrorResponses = {
  ...ExternalAuthErrorResponses,
  403: ExternalErrorSchema.describe('The user is not an external API user, or has no access to this declaration'),
  404: ExternalErrorSchema.describe('Declaration or goods item not found, or the chat session does not belong to this goods item'),
  502: ExternalErrorSchema.describe('The AI chat service is unavailable'),
  503: ExternalErrorSchema.describe('The AI chat is not enabled'),
};

/** Forwarder only: the risk analysis AI chat about one goods item, the same conversation users see in the app. */
export const externalAiChatRoutes = c.router({
  openItemAiChat: {
    method: 'POST',
    path: '/external/agent/goods-item/chat/session',
    summary: 'Open the AI chat of a goods item',
    description:
      'Open the risk analysis AI chat about one goods item and return its conversation so far. There is one conversation per goods item, ' +
      'shared with every user who can see the declaration. Use the returned sessionId to ask questions. ' +
      AI_CHAT_ITEM_ACCESS,
    metadata: { tag: ExternalTag.AiChat } satisfies ExternalRouteMetadata,
    body: AiChatItemBodySchema,
    responses: {
      200: ItemAiChatSessionSchema.describe('The conversation of the goods item, oldest message first'),
      ...AiChatErrorResponses,
    },
  },
  sendItemAiChatMessage: {
    method: 'POST',
    path: '/external/agent/goods-item/chat/message',
    summary: 'Ask the AI chat about a goods item',
    description:
      'Ask a question about the risk analysis of a goods item, for example why a check failed or how to fix it. ' +
      'The answer is in markdown and is saved in the conversation of the goods item. ' +
      AI_CHAT_ITEM_ACCESS,
    metadata: { tag: ExternalTag.AiChat } satisfies ExternalRouteMetadata,
    body: AiChatItemBodySchema.extend({
      sessionId: z.string().uuid().describe('sessionId returned by the session endpoint for this goods item'),
      question: z.string().trim().min(1).max(2000).describe('the question, up to 2000 characters'),
    }),
    responses: {
      200: ItemAiChatTurnSchema.describe('The answer, and the message as saved in the conversation'),
      ...AiChatErrorResponses,
      422: ExternalErrorSchema.describe('The answer was blocked by a safety check'),
    },
  },
});
